import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  GetEventAccountDocument,
  useGetEventAccountQuery,
} from '../../../graphql/queries/getEventAccount.generated';
import { EventList } from '../components/EventList';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { useHistory } from 'react-router';
import { useCreateAuthorizedSignerMutation } from '../../../graphql/mutations/createAuthorizedSigner.generated';
import OrderFulfillmentsTableWithFilter from '../components/OrderFulfillmentsTableWithFilter';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import EventAccountUserTable from './components/UserTable';
import i18n from '@/utils/i18n';
import { useDeleteEventAccountMutation } from '../../../graphql/mutations/deleteEventAccount.generated';
import { useSetPartnerAccountInventoryCheckBillingPolicyMutation } from '../../../graphql/mutations/setPartnerAccountInventoryCheckBillingPolicy.generated';
import { useSetPartnerAccountPurchasingOrderPolicyMutation } from '../../../graphql/mutations/setPartnerAccountPurchasingOrderPolicy.generated';
import { useSetPartnerAccountContainerPolicyMutation } from '../../../graphql/mutations/setPartnerAccountContainerPolicy.generated';
import Checkbox from '../../../components/Checkbox';
import { AuthorizedSignerTable } from './components/AuthorizedSignerTable';
import { ItemLocationTable } from './components/ItemLocationTable';
import { TabsComponentBootstrap } from '../../../components/Layout';
import { getActiveTabFromNav } from '@/utils/url';
import { ContentSection } from '../../../components/Layout/ContentSection';
import { MainHeader } from '../../../components/Layout/MainHeader';
import { FaTrash } from 'react-icons/fa';
import HelpPopover from '../../../components/HelpPopover';
import { EventAccount } from '../../../graphql/generatedTypes';
import { Communicator } from '../../../tools/Communication';
import { ContainerType } from '../OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import { OrderFulfillmentsTableHeaderSort } from '../components/OrderFulfillmentsTable';
import { EventAccountPricesTab } from './components/EventAccoutPricesTab';
import { ZohoEventAccountButton } from '../../../components/Button/ZohoEventAccountButton';
import { DeletedIndicator } from '../../../components/DeletedIndicator';
import { Routes } from '@/utils/routes';
import { EventParticipationsTab } from './EventAccountDetailPageTabs/EventParticipationsTab';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const CONTAINER_POLICY_LABEL_WIDTH = '14rem';

/**
 * A container setting the user has typed into, or `untouched` while the input still shows
 * what the account has stored.
 */
type EditedSetting = { typed: string } | undefined;
const untouched = undefined;

/** `undefined` omits the argument, which is what leaves the stored value alone. */
function asMutationArgument(setting: EditedSetting): number | null | undefined {
  if (setting === untouched) return undefined;
  const typed = setting.typed.trim();
  const parsed = Number(typed);
  return typed === '' || !Number.isFinite(parsed) ? null : parsed;
}

const OfTableHeaderSort: OrderFulfillmentsTableHeaderSort[] = [
  'event',
  'itemLocation',
  'signer',
  'timestamp',
  'type',
  'status',
  'itemCount',
  'detail',
];

export const EventAccountDetailPage = () => {
  const history = useHistory();
  const { eventAccountId, partnerAccountId } = useParams<{
    eventAccountId?: string;
    partnerAccountId?: string;
  }>();
  const accountId = eventAccountId ?? partnerAccountId!;

  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);

  const {
    data,
    loading: loadingEventAccount,
    error,
  } = useGetEventAccountQuery({
    variables: { id: accountId },
    fetchPolicy: 'network-only',
  });

  useDocumentTitle('PageTitles:eventAccount', data?.eventAccount?.name);

  useEffect(() => {
    Communicator.Get('api/3/containers/getAllContainerTypes', {})
      .then((response) => response.json())
      .then((data) => {
        setContainerTypes(data);
      });
  }, []);

  const eventAccount = data?.eventAccount;

  const [deleteEventAccount] = useDeleteEventAccountMutation();

  const [createSigner, { loading: creatingSigner }] =
    useCreateAuthorizedSignerMutation();

  const [
    setInventoryCheckBillingPolicy,
    { loading: settingInventoryCheckBillingPolicy },
  ] = useSetPartnerAccountInventoryCheckBillingPolicyMutation();

  const [setPurchasingOrderPolicy, { loading: settingPurchasingOrderPolicy }] =
    useSetPartnerAccountPurchasingOrderPolicyMutation();
  const [purchasingOrderEmailDraft, setPurchasingOrderEmailDraft] = useState<
    string | null
  >(null);

  async function savePurchasingOrderPolicy(
    needsPurchasingOrderNumber: boolean,
    purchasingOrderEmail: string | null,
  ) {
    if (settingPurchasingOrderPolicy) return;

    const result = await setPurchasingOrderPolicy({
      variables: {
        event_account_id: accountId,
        needs_purchasing_order_number: needsPurchasingOrderNumber,
        purchasing_order_email: needsPurchasingOrderNumber
          ? purchasingOrderEmail
          : null,
      },
      refetchQueries: [
        {
          query: GetEventAccountDocument,
          variables: { id: accountId },
        },
      ],
    }).catch(() => null);

    if (!result || result.errors) {
      LayoutStoreInstance.setError(
        i18n.t('EventAccount:purchasingOrderPolicyError'),
      );
    } else {
      setPurchasingOrderEmailDraft(null);
      LayoutStoreInstance.setSuccess(
        i18n.t('EventAccount:purchasingOrderPolicyUpdated'),
      );
    }
  }

  const [setContainerPolicy, { loading: settingContainerPolicy }] =
    useSetPartnerAccountContainerPolicyMutation();
  const [offlineCardCheckoutLimitDraft, setOfflineCardCheckoutLimitDraft] =
    useState<EditedSetting>(untouched);
  const [returnDeadlineDaysDraft, setReturnDeadlineDaysDraft] =
    useState<EditedSetting>(untouched);

  async function saveContainerPolicy() {
    if (settingContainerPolicy) return;

    const result = await setContainerPolicy({
      variables: {
        event_account_id: accountId,
        offline_card_checkout_limit: asMutationArgument(
          offlineCardCheckoutLimitDraft,
        ),
        return_deadline_days: asMutationArgument(returnDeadlineDaysDraft),
      },
      refetchQueries: [
        {
          query: GetEventAccountDocument,
          variables: { id: accountId },
        },
      ],
    }).catch(() => null);

    if (!result || result.errors) {
      LayoutStoreInstance.setError(i18n.t('EventAccount:containerPolicyError'));
    } else {
      setOfflineCardCheckoutLimitDraft(untouched);
      setReturnDeadlineDaysDraft(untouched);
      LayoutStoreInstance.setSuccess(
        i18n.t('EventAccount:containerPolicyUpdated'),
      );
    }
  }

  function handleTogglePurchasingOrderPolicy() {
    savePurchasingOrderPolicy(
      !eventAccount?.needs_purchasing_order_number,
      null,
    );
  }

  async function handleToggleInventoryCheckBillingPolicy() {
    if (settingInventoryCheckBillingPolicy) return;

    const result = await setInventoryCheckBillingPolicy({
      variables: {
        event_account_id: accountId,
        new_inventory_checks_are_non_billable:
          !eventAccount?.new_inventory_checks_are_non_billable,
      },
      refetchQueries: [
        {
          query: GetEventAccountDocument,
          variables: { id: accountId },
        },
      ],
    });

    if (result.errors) {
      LayoutStoreInstance.setError(
        i18n.t('EventAccount:inventoryCheckBillingPolicyError'),
      );
    } else {
      LayoutStoreInstance.setSuccess(
        i18n.t('EventAccount:inventoryCheckBillingPolicyUpdated'),
      );
    }
  }

  async function handleCreateSigner() {
    if (!accountId) return;

    const result = await createSigner({
      variables: {
        name: 'New ' + eventAccount?.name + ' authorized signer',
        event_account_id: accountId,
      },
      refetchQueries: [
        {
          query: GetEventAccountDocument,
          variables: { id: accountId },
        },
      ],
    });

    if (result.errors) {
      LayoutStoreInstance.setError('Error creating location');
    } else {
      if (result?.data?.createAuthorizedSigner?.id) {
        history.push(
          Routes.authorizedSigner(result?.data?.createAuthorizedSigner?.id),
        );
      }
    }
  }

  if (loadingEventAccount) return <div>{i18n.t('Common:loading')}</div>;
  if (error)
    return (
      <div>
        {i18n.t('Common:error')}: {error.message}
      </div>
    );

  const participatingInEvents = eventAccount?.participating_in_events || [];

  const tabs = [
    {
      title: i18n.t('EventAccount:orderFulfillments'),
      content: (
        <div>
          <OrderFulfillmentsTableWithFilter
            filter={{ event_account_id: accountId }}
            authorizedSigners={
              eventAccount?.authorized_signers.map((s) => ({
                id: s.id,
                name: s.name,
              })) || []
            }
            events={participatingInEvents.map((p) => ({
              id: p.id,
              name: p.name || i18n.t('Common:unknown'),
            }))}
            itemLocations={
              eventAccount?.item_locations?.map((l) => ({
                id: l.id,
                name: l.name,
              })) || []
            }
            eventAccounts={[
              {
                id: eventAccount?.id || i18n.t('Common:unknown'),
                name: eventAccount?.name || i18n.t('Common:unknown'),
              },
            ]}
            containerTypes={containerTypes}
            headers={OfTableHeaderSort}
          />
        </div>
      ),
    },
    {
      title: i18n.t('EventAccount:eventUsers'),
      content: (
        <EventAccountUserTable
          users={eventAccount?.users || []}
          loading={loadingEventAccount}
          error={error != null}
          eventAccountId={accountId}
        />
      ),
    },
    {
      title: i18n.t('EventAccount:authorizedSigners'),
      content: (
        <AuthorizedSignerTable
          eventAccount={eventAccount}
          creatingSigner={creatingSigner}
          handleCreateSigner={handleCreateSigner}
        />
      ),
    },
    {
      title: i18n.t('EventAccount:itemLocations'),
      content: <ItemLocationTable eventAccount={eventAccount} />,
    },
    {
      title: i18n.t('EventAccount:eventParticipations'),
      content: (
        <EventParticipationsTab
          eventAccountId={accountId}
          eventAccount={eventAccount}
        />
      ),
    },
    {
      title: i18n.t('EventAccount:eventPrices'),
      content: (
        <EventAccountPricesTab
          eventAccountId={accountId}
          events={participatingInEvents}
        />
      ),
    },
  ];

  const activeTabString = getActiveTabFromNav();
  const activeTab = activeTabString?.split('_')[0];

  return (
    <div>
      <MainHeader
        title={
          <>
            {`Partner Account "${eventAccount?.name}"`}
            {eventAccount?.is_event_account && (
              <span
                className="badge bg-info ms-2 align-middle"
                style={{ fontSize: '0.6em' }}
              >
                {i18n.t('EventAccount:eventAccountBadge')}
              </span>
            )}
            <DeletedIndicator isDeleted={eventAccount?.is_deleted || false} />
          </>
        }
        rightButton={
          data?.eventAccount?.external_zoho_id
            ? undefined
            : {
                label: 'Edit',
                link: '/event-account/edit/' + accountId,
              }
        }
      />
      <div className="d-flex justify-content-end">
        {data?.eventAccount?.external_zoho_id != null && (
          <>
            <ZohoEventAccountButton
              externalZohoId={data?.eventAccount?.external_zoho_id}
            />
            <div style={{ width: '10px' }} />
          </>
        )}
        {!data?.eventAccount?.external_zoho_id &&
          !data?.eventAccount?.is_deleted && (
            <Button
              onClick={() => setShowRemoveModal(true)}
              size="sm"
              color="danger"
            >
              <FaTrash /> {i18n.t('Common:delete')}
            </Button>
          )}
      </div>
      <Modal
        isOpen={showRemoveModal}
        toggle={() => {
          setShowRemoveModal(false);
        }}
      >
        <ModalHeader
          toggle={() => {
            setShowRemoveModal(false);
          }}
        >
          {i18n.t('EventAccount:modalTitle')}
        </ModalHeader>
        <ModalBody>
          {i18n.t('EventAccount:modalBody')}: <p></p>
          {i18n.t('EventAccount:eventAccount')}: <p>{eventAccount?.name}</p>
          {i18n.t('Common:email')}: <p>{eventAccount?.email}</p>
          {i18n.t('Common:description')}: <p>{eventAccount?.description}</p>
          {i18n.t('EventAccount:users')}: <p>{eventAccount?.users?.length}</p>
          {i18n.t('EventAccount:authorizedSigners')}:{' '}
          <p>{eventAccount?.authorized_signers?.length}</p>
          {i18n.t('EventAccount:events')}:
          <p>
            {eventAccount?.participating_in_events?.map((event) => {
              return (
                <React.Fragment key={event.id}>
                  {event.name}
                  <br />
                </React.Fragment>
              );
            }) || '-'}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={() => {
              deleteEventAccount({
                variables: {
                  event_account_id: accountId,
                },
                onCompleted: () => {
                  setShowRemoveModal(false);

                  LayoutStoreInstance.setSuccess(
                    i18n.t('EventAccount:deletedEventAccountSuccess'),
                  );
                  history.push(Routes.eventAccounts());
                },
                onError: (error) => {
                  setShowRemoveModal(false);

                  LayoutStoreInstance.setError(
                    i18n.t('EventAccount:deletedEventAccountError') + error,
                  );
                  console.error(error);
                },
              });
            }}
          >
            {i18n.t('Common:yes')}
          </Button>{' '}
          <Button
            color="secondary"
            onClick={() => {
              setShowRemoveModal(false);
            }}
          >
            {i18n.t('Common:no')}
          </Button>
        </ModalFooter>
      </Modal>
      <br />

      <ContentSection title={i18n.t('Common:generalInfo')}>
        <div className="card border-0 bg-transparent">
          <div className="card-body">
            <div className="mb-2">
              <strong className="me-2">
                {i18n.t('Common:debitorAccountNumber')}:
              </strong>
              <span className="badge bg-secondary">
                {eventAccount?.debitor_account_number}
              </span>
            </div>
            <div className="mb-2">
              <strong className="me-2">
                {i18n.t('EventAccount:sendOrderfulfillmentsEmails')}:
              </strong>
              <span
                className={`badge ${
                  eventAccount?.send_order_fulfillment_emails
                    ? 'bg-success'
                    : 'bg-secondary'
                }`}
              >
                {eventAccount?.send_order_fulfillment_emails === true
                  ? i18n.t('Common:yes')
                  : i18n.t('Common:no')}
              </span>
            </div>
            {eventAccount?.description && (
              <div className="mb-2">
                <strong className="me-2">
                  {i18n.t('Common:description')}:
                </strong>
                <span>{eventAccount?.description}</span>
              </div>
            )}

            <br />
            <h5 className="d-flex align-items-center fw-bold mt-3">
              {i18n.t('EventAccount:eventBillingInfo')}
              <HelpPopover
                id="event-billing-info-help"
                size="sm"
                text={i18n.t('EventAccount:eventBillingInfoHelp')}
              />
            </h5>
            <div className="mb-2">
              <strong className="me-2">
                {i18n.t('EventAccount:isBillable')}:
              </strong>
              <span
                className={`badge ${
                  eventAccount?.is_billable ? 'bg-success' : 'bg-secondary'
                }`}
              >
                {eventAccount?.is_billable === true
                  ? i18n.t('Common:yes')
                  : i18n.t('Common:no')}
              </span>
            </div>
            <div className="mb-2">
              <strong className="me-2">
                {i18n.t('EventAccount:invoiceLanguage')}:
              </strong>
              <span className="badge bg-info">
                {i18n.t('Common:' + data?.eventAccount?.invoice_language)}
              </span>
            </div>
            <div className="mb-2">
              <strong className="me-2">{i18n.t('Common:email')}:</strong>
              <span className="text-break">{eventAccount?.email || '-'}</span>
            </div>

            <br />
            <h5 className="d-flex align-items-center fw-bold mt-3">
              {i18n.t('EventAccount:inventoryCheckBillingInfo')}
              <HelpPopover
                id="inventory-check-billing-info-help"
                size="sm"
                text={i18n.t('EventAccount:inventoryCheckBillingInfoHelp')}
              />
            </h5>
            <div className="d-flex align-items-center mb-2">
              <Checkbox
                checked={
                  eventAccount?.new_inventory_checks_are_non_billable ?? false
                }
                disabled={settingInventoryCheckBillingPolicy}
                onClick={handleToggleInventoryCheckBillingPolicy}
              />
              <span
                className="ms-2"
                role="button"
                onClick={handleToggleInventoryCheckBillingPolicy}
              >
                {i18n.t('EventAccount:newInventoryChecksAreNonBillable')}
              </span>
            </div>

            <br />
            <h5 className="d-flex align-items-center fw-bold mt-3">
              {i18n.t('EventAccount:purchasingOrderInfo')}
              <HelpPopover
                id="purchasing-order-info-help"
                size="sm"
                text={i18n.t('EventAccount:purchasingOrderInfoHelp')}
              />
            </h5>
            <div className="d-flex align-items-center mb-2">
              <Checkbox
                checked={eventAccount?.needs_purchasing_order_number ?? false}
                disabled={settingPurchasingOrderPolicy}
                onClick={handleTogglePurchasingOrderPolicy}
              />
              <span
                className="ms-2"
                role="button"
                onClick={handleTogglePurchasingOrderPolicy}
              >
                {i18n.t('EventAccount:needsPurchasingOrderNumber')}
              </span>
            </div>
            <div className="input-group mb-2">
              <span className="input-group-text">
                {i18n.t('EventAccount:purchasingOrderEmail')}
              </span>
              <input
                type="text"
                className="form-control"
                autoComplete="off"
                disabled={
                  !eventAccount?.needs_purchasing_order_number ||
                  settingPurchasingOrderPolicy
                }
                value={
                  purchasingOrderEmailDraft ??
                  eventAccount?.purchasing_order_email ??
                  ''
                }
                onChange={(e) => setPurchasingOrderEmailDraft(e.target.value)}
                placeholder={'po1@example.com;po2@example.com'}
              />
              <Button
                color="primary"
                disabled={
                  purchasingOrderEmailDraft == null ||
                  settingPurchasingOrderPolicy ||
                  !eventAccount?.needs_purchasing_order_number
                }
                onClick={() =>
                  savePurchasingOrderPolicy(
                    true,
                    (purchasingOrderEmailDraft || '').trim() || null,
                  )
                }
              >
                {i18n.t('Common:save')}
              </Button>
            </div>

            <br />
            <h5 className="d-flex align-items-center fw-bold mt-3">
              {i18n.t('EventAccount:containerPolicyInfo')}
              <HelpPopover
                id="container-policy-info-help"
                size="sm"
                text={i18n.t('EventAccount:containerPolicyInfoHelp')}
              />
            </h5>
            <div className="input-group mb-2">
              <span
                className="input-group-text"
                style={{ minWidth: CONTAINER_POLICY_LABEL_WIDTH }}
              >
                {i18n.t('EventAccount:offlineCardCheckoutLimit')}
              </span>
              <input
                type="number"
                min={1}
                max={100}
                step={1}
                className="form-control"
                autoComplete="off"
                disabled={settingContainerPolicy}
                value={
                  offlineCardCheckoutLimitDraft?.typed ??
                  eventAccount?.offline_card_checkout_limit ??
                  ''
                }
                onChange={(e) =>
                  setOfflineCardCheckoutLimitDraft({ typed: e.target.value })
                }
                placeholder={i18n.t('EventAccount:containerPolicyInherited')}
              />
            </div>
            <div className="input-group mb-2">
              <span
                className="input-group-text"
                style={{ minWidth: CONTAINER_POLICY_LABEL_WIDTH }}
              >
                {i18n.t('EventAccount:returnDeadlineDays')}
              </span>
              <input
                type="number"
                min={1}
                max={365}
                step={1}
                className="form-control"
                autoComplete="off"
                disabled={settingContainerPolicy}
                value={
                  returnDeadlineDaysDraft?.typed ??
                  eventAccount?.return_deadline_days ??
                  ''
                }
                onChange={(e) =>
                  setReturnDeadlineDaysDraft({ typed: e.target.value })
                }
                placeholder={i18n.t('EventAccount:containerPolicyInherited')}
              />
            </div>
            <Button
              color="primary"
              disabled={
                (offlineCardCheckoutLimitDraft === untouched &&
                  returnDeadlineDaysDraft === untouched) ||
                settingContainerPolicy
              }
              onClick={saveContainerPolicy}
            >
              {i18n.t('Common:save')}
            </Button>
          </div>
        </div>
      </ContentSection>

      <ContentSection title={i18n.t('EventAccount:stores')} collapsed>
        {eventAccount?.stores && eventAccount.stores.length > 0 ? (
          <table className="table-bordered table-striped table">
            <thead>
              <tr>
                <th>{i18n.t('Common:name')}</th>
                <th>{i18n.t('Common:address')}</th>
                <th>{i18n.t('Common:status')}</th>
              </tr>
            </thead>
            <tbody>
              {eventAccount.stores.map((store) => (
                <tr
                  key={store.id}
                  className={store.permanently_removed ? 'opacity-50' : ''}
                >
                  <td>
                    <Link to={Routes.partnerStore(store.id)}>
                      {store.name || store.id}
                    </Link>
                  </td>
                  <td>
                    {store.address ? (
                      <span className="text-muted">
                        {store.address.streetname} {store.address.streetno},{' '}
                        {store.address.zip} {store.address.city}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {store.permanently_removed && (
                      <span className="badge bg-danger">
                        {i18n.t('PartnerSearch:deleted')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>{i18n.t('EventAccount:noStores')}</p>
        )}
      </ContentSection>

      <ContentSection title={i18n.t('EventAccount:events')} collapsed>
        {participatingInEvents && participatingInEvents.length > 0 ? (
          <div>
            <EventList
              events={participatingInEvents}
              eventAccount={eventAccount as EventAccount}
              showGoToDepositClearing={true}
              showDeposit={true}
            />
          </div>
        ) : (
          <p>{i18n.t('EventAccount:notParticipating')}</p>
        )}
      </ContentSection>
      <ContentSection title={i18n.t('EventAccount:info')}>
        <TabsComponentBootstrap
          tabs={tabs}
          activeTab={parseInt(activeTab, 10)}
        />
      </ContentSection>
    </div>
  );
};
